import { MediaQueryWrapper, TRANSLATIONS_APP_NAME, useIsMobileHook } from '@telia-common-components/shared';
import * as React from 'react';
import { Card, CardContent, Container, Grid, GridColumn, Heading, List, ListItem, Notice, Separator, TvComparison, useAnalytics, useTheme, } from 'telia-front-react';
import ChannelSearch from '../../common/ChannelSearch';
import { createServicesGroup, filterServices, mapAdditionalServices } from '../../util/TvComparisonAdditionalServiceMapper';
import { APP_NAMES, createAppsGroup, filterApps, mapApps } from '../../util/TvComparisonAppMapper';
import { createChannelsGroup, createChannelsMap, mapChannelsInfo } from '../../util/TvComparisonChannelMapper';
import { filterChannelsByCategories, filterChannelsNotAvailableInSelectedPackages, filterThemesByCategories, filterVisibleThemes, mapChannelResults, mapThemeResults, resolveVisiblePacketNames, } from '../../util/TvComparisonFilterUtil';
import { getIconByService } from '../../util/TvComparisonIconMapper';
import { mapPacketsAndPrices } from '../../util/TvComparisonPacketsMapper';
import { createThemePackagesGroup, mapThemePackages } from '../../util/TvComparisonThemePackageMapper';
import { createViewerSlotsGroup } from '../../util/TvComparisonViewerSlotsMapper';
import TvComparisonFilter from './TvComparisonFilter';
import TvComparisonSkeleton from './TvComparisonSkeleton';
import { useEffect, useState } from 'react';
const TvComparisonModalContent = (props) => {
    var _a, _b, _c;
    const { translate, translations, icons, filterState, filterActions, shownChannelsCount, shownThemesCount, shownServicesCount, shownAppsCount, urlData, headingText, headingLevel, headingIsSrOnlyInDesktop, isModal, } = props;
    const { packagePrices, packageNames, categoriesList, viewerSlots, additionalServices, channels, themePackages, apps } = props.packagesResponse;
    const { push: pushAnalytics } = useAnalytics();
    const { isMobile, isMobileOrTablet } = useIsMobileHook();
    const { media } = useTheme();
    const [searchStatus, setSearchStatus] = useState('');
    const isSearch = !!((_a = filterState.selectedItem) === null || _a === void 0 ? void 0 : _a.label);
    useEffect(() => {
        const selectedPackages = filterState.selectedPackages || [];
        const selectedThemes = filterState.selectedThemes || [];
        const selectedApps = filterState.selectedApps || [];
        const selectedServices = filterState.selectedServices || [];
        const filtersChanged = selectedPackages.length || selectedThemes.length || selectedApps.length || selectedServices.length;
        if (isSearch || filtersChanged) {
            setSearchStatus(translate('tvcomparison.modalContent.searchResults', {
                count: getSearchResultsAmount(),
            }));
        }
        else {
            setSearchStatus('');
        }
    }, [isSearch, filterState.selectedItem, filterState.selectedPackages, filterState.selectedThemes, filterState.selectedApps, filterState.selectedServices]);
    if (!packageNames.length || !channels.length || !(translations === null || translations === void 0 ? void 0 : translations[TRANSLATIONS_APP_NAME])) {
        return React.createElement(TvComparisonSkeleton, { translate: translate });
    }
    const getIcon = (service) => {
        return getIconByService(service, icons);
    };
    const packets = mapPacketsAndPrices(packagePrices, filterState.selectedPackages.length ? filterState.selectedPackages : packageNames, translate);
    const visiblePackageNames = resolveVisiblePacketNames(packageNames, filterState.selectedPackages.length ? filterState.selectedPackages : packageNames, packagePrices);
    const channelsMap = createChannelsMap(channels);
    let filteredThemes = [];
    let filteredChannels = [];
    let filteredServices;
    let filteredApps = [];
    const commonMapperProps = {
        packageNames: visiblePackageNames,
        translate,
        pushAnalytics,
        urlData,
        pricePerMonthLabel: 'tvcomparison.price.permonth',
        getIcon,
    };
    const hideSectionIfNoFilterSelections = (selections, section) => {
        if (!selections.length) {
            if (Array.isArray(section)) {
                section = [];
            }
            else {
                section = {};
            }
        }
        return section;
    };
    if ((themePackages === null || themePackages === void 0 ? void 0 : themePackages.length) || (channels === null || channels === void 0 ? void 0 : channels.length)) {
        let channelsResultMap = mapChannelsInfo(channels, {
            ...commonMapperProps,
            identifier: 'ALL',
        });
        let themesResultMap = mapThemePackages(themePackages, channelsMap, {
            ...commonMapperProps,
            identifier: 'ALL',
        });
        let appResults = apps.slice();
        let serviceResults = additionalServices.slice();
        if (filterState.selectedThemes.length && !isSearch) {
            channelsResultMap = filterChannelsByCategories(filterState.selectedThemes, categoriesList, channelsResultMap);
            themesResultMap = filterThemesByCategories(filterState.selectedThemes, categoriesList, themePackages, themesResultMap);
            appResults = hideSectionIfNoFilterSelections(filterState.selectedApps, appResults);
            serviceResults = hideSectionIfNoFilterSelections(filterState.selectedServices, serviceResults);
        }
        if (((_b = filterState.selectedServices) === null || _b === void 0 ? void 0 : _b.length) && !isSearch) {
            serviceResults = filterServices(additionalServices, filterState.selectedServices);
            channelsResultMap = hideSectionIfNoFilterSelections(filterState.selectedThemes, channelsResultMap);
            themesResultMap = hideSectionIfNoFilterSelections(filterState.selectedThemes, themesResultMap);
            appResults = hideSectionIfNoFilterSelections(filterState.selectedApps, appResults);
        }
        if (filterState.selectedApps.length && !isSearch) {
            appResults = filterApps(apps, filterState.selectedApps);
            channelsResultMap = hideSectionIfNoFilterSelections(filterState.selectedThemes, channelsResultMap);
            themesResultMap = hideSectionIfNoFilterSelections(filterState.selectedThemes, themesResultMap);
            serviceResults = hideSectionIfNoFilterSelections(filterState.selectedServices, serviceResults);
        }
        filteredThemes = mapThemeResults(themesResultMap, themePackages);
        filteredChannels = mapChannelResults(channelsResultMap, channels);
        filteredServices = mapAdditionalServices(serviceResults, commonMapperProps);
        filteredApps = mapApps(appResults, commonMapperProps);
    }
    if (isSearch) {
        filteredThemes = filteredThemes.filter((theme) => theme.channels.some((channel) => channel.title.toUpperCase().includes(filterState.selectedItem.label.toUpperCase())));
        filteredChannels = (_c = filteredChannels === null || filteredChannels === void 0 ? void 0 : filteredChannels.slice()) === null || _c === void 0 ? void 0 : _c.filter((c) => { var _a, _b; return (_a = c.title) === null || _a === void 0 ? void 0 : _a.toUpperCase().includes((((_b = filterState.selectedItem) === null || _b === void 0 ? void 0 : _b.label) || '').toUpperCase()); });
        filteredServices = filteredServices.filter((service) => {
            var _a;
            return service.title.toUpperCase().includes(filterState.selectedItem.label.toUpperCase()) ||
                ((_a = service.channelIds) === null || _a === void 0 ? void 0 : _a.some((id) => channelsMap[id].title.toUpperCase().includes(filterState.selectedItem.label.toUpperCase())));
        });
        filteredApps = filteredApps.filter((app) => app.title.toUpperCase().includes(filterState.selectedItem.label.toUpperCase()));
    }
    filteredChannels = filterChannelsNotAvailableInSelectedPackages(visiblePackageNames, packageNames, filteredChannels);
    const commonCreateGroupProps = { translate, filterActions };
    const themePacketsGroup = createThemePackagesGroup({
        ...commonCreateGroupProps,
        themes: filteredThemes,
        isAllShown: filterState.isAllThemesShown,
        shownItemsCount: shownThemesCount,
    });
    const channelsGroup = createChannelsGroup({
        ...commonCreateGroupProps,
        channels: filteredChannels,
        isAllShown: filterState.isAllChannelsShown,
        shownItemsCount: shownChannelsCount,
    });
    const viewerSlotsGroup = createViewerSlotsGroup(viewerSlots, commonMapperProps);
    const serviceGroup = createServicesGroup({
        ...commonCreateGroupProps,
        services: filteredServices,
        viewerSlotsAmount: viewerSlotsGroup.slots.length,
        isAllShown: filterState.isAllServicesShown,
        shownItemsCount: shownServicesCount,
    });
    const appsGroup = createAppsGroup({
        ...commonCreateGroupProps,
        apps: filteredApps,
        isAllShown: filterState.isAllAppsShown,
        shownItemsCount: shownAppsCount,
    });
    const getSearchResultsAmount = () => {
        return filteredChannels.length + filteredThemes.length + filteredServices.length + filteredApps.length + (showViewerSlots() ? 1 : 0);
    };
    const showViewerSlots = () => {
        var _a;
        const isAnyotherFilterActive = filterState.selectedThemes.length + filterState.selectedApps.length;
        return !isSearch && ((!isAnyotherFilterActive && !((_a = filterState.selectedServices) === null || _a === void 0 ? void 0 : _a.length)) || filterState.selectedServices.includes('viewerSlots'));
    };
    const renderTvComparisonTable = () => {
        return (React.createElement(TvComparison, { sections: [
                { type: 'channels', data: channelsGroup },
                { type: 'apps', data: appsGroup },
                { type: 'serviceGroups', data: serviceGroup },
                { type: 'themePackets', data: themePacketsGroup },
                showViewerSlots() ? { type: 'viewerSlots', data: viewerSlotsGroup } : { type: 'viewerSlots', data: { ...viewerSlotsGroup, slots: [] } },
            ], packets: packets, soundLabel: `${translate('tvcomparison.channels.audio')}`, subTitlesLabel: `${translate('tvcomparison.channels.subtitles')}`, initialHeadingLevel: headingLevel, scrollLeftButton: {
                text: translate('tvcomparison.table.scroll.previous'),
                onClick: () => pushAnalytics('Table - Scroll - Tagasi'),
            }, scrollRightButton: {
                text: translate('tvcomparison.table.scroll.next'),
                onClick: () => pushAnalytics('Table - Scroll - Edasi'),
            }, onDetailsInfoToggle: (id, element, isOpen) => pushAnalytics(`Link - ${element} - ${id} - avatud: ${isOpen}`) }));
    };
    const renderNoResults = () => {
        var _a;
        return (React.createElement(Container, null,
            React.createElement(Separator, { color: "invisible", spacing: "xsmall" }),
            ((_a = filterState.selectedItem) === null || _a === void 0 ? void 0 : _a.label) ? (React.createElement(React.Fragment, null,
                React.createElement("p", { className: "text-center" }, translate('tvcomparison.search.noResults', {
                    search: filterState.selectedItem.label,
                })),
                React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
                React.createElement(Grid, { align: ['center-xs'] },
                    React.createElement(GridColumn, { width: ['xs-12', 'md-8', 'lg-7', 'xl-5'] },
                        React.createElement(Card, { type: "light", padding: isMobile ? 'medium' : 'xxlarge', background: "neutral-bg-level-1" },
                            React.createElement(CardContent, null,
                                React.createElement("p", { className: "h5 text-bold text-left" }, translate('tvcomparison.search.noResults.check')),
                                React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
                                React.createElement(List, { className: "text-left", type: "icon", icon: "#check" },
                                    React.createElement(ListItem, null, translate('tvcomparison.search.noResults.check.item.1')),
                                    React.createElement(ListItem, null, translate('tvcomparison.search.noResults.check.item.2'))))))))) : (React.createElement("p", { className: "text-center" }, translate('tvcomparison.noResults')))));
    };
    const renderSearchAndFilters = () => {
        const searchAndFilters = (React.createElement(React.Fragment, null,
            React.createElement(Grid, { align: ['between-lg'] },
                React.createElement(GridColumn, { width: ['lg-4', 'md-6'] },
                    React.createElement(ChannelSearch, { translate: translate, suggestions: filterState.channelSuggestionItems, onSelect: filterActions.selectSuggestionItem, fuzzySearch: filterState.fuzzySearch, selectedItem: filterState.selectedItem })),
                React.createElement("span", { className: "sr-only", "aria-live": "polite" }, searchStatus),
                React.createElement(GridColumn, { width: ['min-lg', 'md-12'], align: ['bottom-lg'] },
                    React.createElement(TvComparisonFilter, { packageNames: packagePrices.map((offer) => offer.name), themeNames: filterVisibleThemes(Object.keys(categoriesList || {})), appNames: APP_NAMES, serviceNames: additionalServices.map((service) => service.productOfferingCode).concat('viewerSlots'), translate: translate, filterState: filterState, filterActions: filterActions, hideApps: !apps.length }))),
            React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
            React.createElement(Notice, { size: "small" }, translate('tvcomparison.search.infoMessage'))));
        return React.createElement(MediaQueryWrapper, { minWidth: media.width.lg.min, matchingContent: searchAndFilters, unmatchingContent: React.createElement(Container, null, searchAndFilters) });
    };
    return (React.createElement(Card, { padding: { top: 'small', right: 'none', bottom: 'small', left: 'none' }, responsive: [{ breakPoint: 'lg', props: { padding: 'large' } }], background: "transparent", ...props.card },
        React.createElement(CardContent, null,
            React.createElement("div", null,
                ((isModal && !isMobileOrTablet) || !isModal) && (React.createElement(React.Fragment, null,
                    React.createElement(Heading, { level: headingLevel, className: headingIsSrOnlyInDesktop ? 'sr-only' : 'h2 text-center' }, headingText),
                    !headingIsSrOnlyInDesktop && React.createElement(Separator, { spacing: "xsmall", color: "invisible" }))),
                renderSearchAndFilters(),
                React.createElement(Separator, { spacing: "xsmall", color: "invisible" })),
            React.createElement("div", { "aria-live": "polite", "aria-atomic": true }, getSearchResultsAmount() === 0 && renderNoResults()),
            getSearchResultsAmount() !== 0 && renderTvComparisonTable())));
};
export default TvComparisonModalContent;
